import React, { useRef, useState, useEffect } from "react"

import {
  Container,
  TextContainer,
  TitleRow,
  Title,
  ArrowContainer,
  ArrowLeft,
  ArrowRight,
  Text,
  Cards,
  Card,
  CardImage,
  CardTop,
  CardName,
  ClassTitlesWrapper,
  CardClassTitle,
} from "./styles"

const HCC_ID = 152

const classIdMap = {
  138: { name: "FUNCTIONAL NUTRITION+", color: "#227298" },
  // 139: { name: "HEALTH COACHING+", color: "#E03015" },
  [HCC_ID]: { name: "HEALTH COACHING+", color: "#E03015" },
  157: { name: "PERI/MENOPAUSE+", color: "#A8146C" },
}

const PeopleScrollableGrid = ({ items, title, subtitle, showClassTags }) => {
  const cardsRef = useRef(null)
  const [isAtStart, setIsAtStart] = useState(true)
  const [isAtEnd, setIsAtEnd] = useState(false)

  const handleScroll = () => {
    if (cardsRef.current) {
      const atStart = cardsRef.current.scrollLeft === 0
      const atEnd =
        cardsRef.current.scrollLeft >= cardsRef.current.scrollWidth - cardsRef.current.offsetWidth

      setIsAtStart(atStart)
      setIsAtEnd(atEnd)
    }
  }

  const calculateScrollDistance = () => {
    const CARD_GAP_PIXELS = 15
    const DEFAULT_SCROLL_DISTANCE = 150

    if (!cardsRef.current) return DEFAULT_SCROLL_DISTANCE

    const firstCard = cardsRef.current.children[0]
    if (!firstCard) return DEFAULT_SCROLL_DISTANCE

    // Since we have a 2-row grid, the card in the next column is at index 2
    const nextColumnCard = cardsRef.current.children[2]
    if (nextColumnCard) {
      return nextColumnCard.offsetLeft - firstCard.offsetLeft
    }

    return firstCard.offsetWidth + CARD_GAP_PIXELS
  }

  const moveForward = () => {
    if (cardsRef.current) {
      const scrollDistance = calculateScrollDistance()
      cardsRef.current.scrollBy({ left: scrollDistance, behavior: "smooth" })
    }
  }

  const moveBackward = () => {
    if (cardsRef.current) {
      const scrollDistance = calculateScrollDistance()
      cardsRef.current.scrollBy({ left: -scrollDistance, behavior: "smooth" })
    }
  }

  const handleKeyDown = (event, direction) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      if (direction === "left") {
        moveBackward()
      } else {
        moveForward()
      }
    }
  }

  useEffect(() => {
    const ref = cardsRef.current

    if (ref) {
      ref.addEventListener("scroll", handleScroll)
      handleScroll()
    }

    return () => {
      if (ref) {
        ref.removeEventListener("scroll", handleScroll)
      }
    }
  }, [])

  // Transform data to arrange in correct order (top-to-bottom, then left-to-right)
  const arrangeDataInOrder = (dataItems) => {
    const arranged = []
    const rowCount = 2

    for (let col = 0; col < Math.ceil(dataItems.length / rowCount); col += 1) {
      for (let row = 0; row < rowCount; row += 1) {
        const index = col * rowCount + row
        if (index < dataItems.length) {
          arranged.push(dataItems[index])
        }
      }
    }

    return arranged
  }

  const arrangedItems = arrangeDataInOrder(items)

  return (
    <Container>
      <TextContainer>
        <TitleRow>
          <Title dangerouslySetInnerHTML={{ __html: title }} />
          <ArrowContainer>
            <ArrowLeft
              disabled={isAtStart}
              onClick={moveBackward}
              onKeyDown={(e) => handleKeyDown(e, "left")}
              aria-label="Scroll collective cards left"
              aria-controls="collective-cards"
              aria-disabled={isAtStart}
              tabIndex={0}
            />
            <ArrowRight
              disabled={isAtEnd}
              onClick={moveForward}
              onKeyDown={(e) => handleKeyDown(e, "right")}
              aria-label="Scroll collective cards right"
              aria-controls="collective-cards"
              aria-disabled={isAtEnd}
              tabIndex={0}
            />
          </ArrowContainer>
        </TitleRow>
        {subtitle && <Text dangerouslySetInnerHTML={{ __html: subtitle }} />}
      </TextContainer>

      <Cards
        ref={cardsRef}
        id="collective-cards"
        role="list"
        aria-label="Collective member cards"
        showClassTags={showClassTags}
      >
        {arrangedItems.map((item) => {
          const { name, image, id, classes } = item
          const imageUrl = image && image[0]?.secure_url

          let classTags = showClassTags
            ? classes
                ?.filter((cls) => classIdMap[cls.classId])
                .map((cls) => classIdMap[cls.classId])
            : []

          if (classTags.length > 1) {
            // Temporary solution for showing only one class tag
            // use HCC tag if present in the classTags
            classTags = classTags.some((tag) => tag.name === classIdMap[HCC_ID].name)
              ? [classIdMap[HCC_ID]]
              : classTags.slice(0, 1)
          }

          return (
            <Card key={id || name} role="listitem">
              <CardTop>
                <CardImage
                  baseUrl={imageUrl}
                  alt={name}
                  images={[
                    {
                      transforms: "c_fill,g_auto,w_176,h_176,q_auto,f_auto,fl_lossy",
                      screenSize: 992,
                    },
                    {
                      transforms: "c_fill,g_auto,w_145,h_145,q_auto,f_auto,fl_lossy",
                      screenSize: 768,
                    },
                    {
                      transforms: "c_fill,g_auto,w_95,h_95,q_auto,f_auto,fl_lossy",
                    },
                  ]}
                  lazy
                />
                <CardName>{name}</CardName>
              </CardTop>

              {classTags && showClassTags && (
                <ClassTitlesWrapper>
                  {classTags.map((tag) => (
                    <CardClassTitle key={tag.name} color={tag.color}>
                      {tag.name}
                    </CardClassTitle>
                  ))}
                </ClassTitlesWrapper>
              )}
            </Card>
          )
        })}
      </Cards>
    </Container>
  )
}

export default PeopleScrollableGrid
